import { useState, useEffect } from "react";
import ProfileForm from "./components/form/index";
import { useUser, useUserUpdate } from "../../common/hooks/api/user";
import { toastError, toastSuccess } from "../../helpers/toast-helper";
import useAuthUser from "../../common/hooks/authUser";
import { ScreenOverlay, Spinner } from "../../components/Common";
import { useNavigate } from "react-router-dom";
import { TProfileForm } from "./types";
import { getResponseItemData } from "../../helpers/response-helper";
import { useCompanyLogo, useCompanyLogoUpdate } from "../../common/hooks/api/company";
import { TNullable } from "../../common/types/common";

const UserProfileFormPage = () => {
    const navigate = useNavigate();
    const { user } = useAuthUser();
    const [companyLogo, setCompanyLogo] = useState<TNullable<File>>();
    const [newCompanyLogo, setNewCompanyLogo] = useState<TNullable<File>>();
    const [isUpdateUserToastShowing, setIsUpdateUserToastShowing] = useState<boolean>(false);
    const [isUpdateCompanyLogoToastShowing, setIsUpdateCompanyLogoToastShowing] = useState<boolean>(false);
    const { data: userResponse, isLoading, refetch: refetchUser } = useUser(user.id, {
        onError: () => {
            toastError("Cannot fetch user data");
        },
    });
    const { data } = getResponseItemData(userResponse);

    useCompanyLogo(data?.company?.id, {
        enabled: !!data?.company?.id,
        onSuccess: (imageBlob) => {
            setCompanyLogo(imageBlob);
        },
        onError: () => {
            setCompanyLogo(null);
        },
    });

    const { mutate: updateUser, isPending: isUserUpdatePending, isSuccess: isUserUpdateSuccess } = useUserUpdate();
    const { mutate: updateCompanyLogo, isPending: isCompanyLogoUpdatePending, isSuccess: isCompanyLogoUpdateSuccess } = useCompanyLogoUpdate();

    const onSave = (form: TProfileForm): void => {
        setNewCompanyLogo(form.company.file);

        const payload = {
            ...data,
            ...form,
            company: {
                ...data?.company,
                ...form.company,
                file: undefined,
            },
        };

        updateUser(payload, {
            onSuccess: async () => {
                if (!data?.company?.id) {
                    refetchUser();
                }

                setIsUpdateUserToastShowing(true);
                await toastSuccess("Profile was successfully updated");
                setIsUpdateUserToastShowing(false);
            },
            onError: async () => {
                await toastError("Cannot update the profile");
                setIsUpdateUserToastShowing(false);
            },
        });
    };

    useEffect(() => {
        if (newCompanyLogo && data?.company?.id) {
            const formData = new FormData();
            formData.append("logo", newCompanyLogo);

            updateCompanyLogo({ companyId: data?.company.id, payload: formData }, {
                onSuccess: async () => {
                    setIsUpdateCompanyLogoToastShowing(true);
                    await toastSuccess("Company Logo was successfully updated");
                    setIsUpdateCompanyLogoToastShowing(false);
                },
                onError: async () => {
                    await toastError("Cannot update the company logo");
                    setIsUpdateCompanyLogoToastShowing(false);
                },
            });
        }
    }, [data?.company?.id, newCompanyLogo]);

    useEffect(() => {
        if (!isUserUpdatePending && !isCompanyLogoUpdatePending && !isUpdateUserToastShowing && !isUpdateCompanyLogoToastShowing && isUserUpdateSuccess && isCompanyLogoUpdateSuccess) {
            navigate('/profile');
        }
    }, [isCompanyLogoUpdatePending, isUserUpdatePending, isUpdateUserToastShowing, isUpdateCompanyLogoToastShowing, isUserUpdateSuccess]);

    return (
        <>
            <ProfileForm
                user={data}
                companyLogo={newCompanyLogo || companyLogo}
                isDisabled={isLoading || isUserUpdatePending || isUpdateUserToastShowing || isUpdateCompanyLogoToastShowing || isCompanyLogoUpdatePending}
                onSave={onSave}
            />
            {isLoading && (
                <>
                    <ScreenOverlay />
                    <Spinner />
                </>
            )}
        </>
    );
};

export default UserProfileFormPage;
